import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  InternationalCustomsFlatTeamSettingGroupById,
  type InternationalCustomsFlatTeamSettingGroupId,
} from "./groups";

type InternationalCustomsSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: InternationalCustomsSettingInput,
): InternationalCustomsSettingInput {
  return metadata;
}

function internationalCustomsGroup<
  const Group extends InternationalCustomsFlatTeamSettingGroupId,
  const T extends Record<string, InternationalCustomsSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const InternationalCustomsRegistry = {
  ...internationalCustomsGroup("international_customs.customs_artifact", {
    "customsAugmentation/artifactId": setting({
      label: "Customs artifact",
      description:
        "Custom code artifact that reshapes the line items sent through customs at checkout and fulfillment.",
      synonyms: [
        "customs artifact",
        "customs code",
        "line item artifact",
        "customs augmentation",
      ],
    }),
  }),
  ...internationalCustomsGroup("international_customs.customs_metafields", {
    "customsAugmentation/metafieldKeys/product": setting({
      label: "Product metafield keys",
      description:
        "Product-level metafield keys fetched and exposed to the customs code as item.productMetafields.",
      synonyms: ["customs product metafields", "product metafield keys"],
    }),
    "customsAugmentation/metafieldKeys/variant": setting({
      label: "Variant metafield keys",
      description:
        "Variant-level metafield keys fetched and exposed to the customs code as item.variantMetafields.",
      synonyms: ["customs variant metafields", "variant metafield keys"],
    }),
  }),
};

type InternationalCustomsFlatTeamSettingKey =
  keyof typeof InternationalCustomsRegistry;
const InternationalCustomsFlatTeamSettingKeys = Object.keys(
  InternationalCustomsRegistry,
) as InternationalCustomsFlatTeamSettingKey[];

export const InternationalCustomsFlatTeamSettingCatalogDefinitions =
  InternationalCustomsFlatTeamSettingKeys.map((key) => {
    const definition = InternationalCustomsRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: InternationalCustomsFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    InternationalCustomsFlatTeamSettingKey,
    InternationalCustomsFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<InternationalCustomsFlatTeamSettingKey>
  >[];

export const InternationalCustomsFlatTeamSettingMetadataByKey =
  Object.fromEntries(
    InternationalCustomsFlatTeamSettingCatalogDefinitions.map((definition) => [
      definition.id,
      definition.metadata,
    ]),
  ) as Record<InternationalCustomsFlatTeamSettingKey, SettingMetadata>;
